import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { addTestimonial, signInAnonymouslyForPublicForm } from '../services/firebase';
import { Star, MessageSquare, Send, ArrowLeft, CheckCircle2, User, Briefcase, Image as ImageIcon, Upload, Link as LinkIcon, Loader2 } from 'lucide-react';
import SEO from '../components/SEO';

const PublicTestimonialForm = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '',
    role: '',
    image: '',
    rating: 5,
    text: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [imageSourceType, setImageSourceType] = useState('url');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileError, setFileError] = useState('');

  useEffect(() => {
    // Sign in anonymously when component mounts
    signInAnonymouslyForPublicForm().catch((error) => {
      console.error('Error signing in anonymously:', error);
    });
  }, []);

  // File upload handler with WebP conversion
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 2MB)
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      setFileError('Ukuran file terlalu besar. Maksimal 2MB.');
      setForm({ ...form, image: '' });
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setFileError('File harus berupa gambar.');
      setForm({ ...form, image: '' });
      return;
    }

    setUploadingFile(true);
    setFileError('');

    try {
      // Create an Image object
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.onload = () => {
          // Create canvas for WebP conversion
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);

          // Convert to WebP base64
          const webpDataUrl = canvas.toDataURL('image/webp', 0.85);

          // Check if base64 size is still under limit
          const base64Length = webpDataUrl.length;
          const base64SizeKB = (base64Length * (3/4)) / 1024; // Approximate size in KB

          if (base64SizeKB > 1900) { // Leave some buffer, aim for under 1.9MB
            setFileError('Ukuran file setelah konversi terlalu besar. Silakan gunakan gambar yang lebih kecil atau kompres gambar Anda.');
            setForm({ ...form, image: '' });
          } else {
            setForm({ ...form, image: webpDataUrl });
            setFileError('');
          }
          setUploadingFile(false);
        };

        img.onerror = () => {
          setFileError('Gagal memuat gambar. Silakan coba file lain.');
          setUploadingFile(false);
        };

        img.src = e.target.result;
      };

      reader.onerror = () => {
        setFileError('Gagal membaca file. Silakan coba lagi.');
        setUploadingFile(false);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing image:', error);
      setFileError('Gagal memproses gambar. Silakan coba lagi.');
      setUploadingFile(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await addTestimonial(form);
      setSubmitted(true);
      // Reset form after 3 seconds and navigate home
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error) {
      console.error('Error submitting testimonial:', error);
      alert('Gagal mengirim testimoni. Silakan coba lagi.');
    } finally {
      setSubmitting(false);
    }
  };

  const isFormValid = form.name.trim() && form.role.trim() && form.text.trim() && form.rating > 0;

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A0A0A] to-black flex items-center justify-center p-4">
        {/* Decorative Elements */}
        <div className="absolute top-1/4 left-10 w-96 h-96 bg-[#D4AF7A]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-[#C9A882]/5 rounded-full blur-3xl"></div>

        <div className="relative max-w-md w-full text-center">
          <div className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/10 rounded-full mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-400" />
            </div>
            <h2 className="text-3xl font-bold text-[#F7F5F2] mb-4">
              Terima Kasih!
            </h2>
            <p className="text-[#F7F5F2]/70 mb-6">
              Testimoni Anda berhasil dikirim. Testimoni akan ditampilkan setelah melalui proses review admin.
            </p>
            <p className="text-[#D4AF7A] text-sm">Anda akan diarahkan ke halaman utama...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0A0A] to-black">
      <SEO
        title="Tulis Testimoni | Putri Henna Artist"
        description="Bagikan pengalaman Anda menggunakan layanan henna dari Putri Henna Artist. Testimoni Anda akan membantu orang lain menemukan layanan henna terbaik."
        keywords="testimoni henna, ulasan henna, feedback pelanggan, putri henna, review henna wedding"
        url="https://tehputrihenna.com/testimoni"
      />
      {/* Decorative Elements */}
      <div className="absolute top-1/4 left-10 w-96 h-96 bg-[#D4AF7A]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-[#C9A882]/5 rounded-full blur-3xl"></div>

      {/* Header */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-[#F7F5F2]/70 hover:text-[#D4AF7A] transition-colors mb-8"
        >
          <ArrowLeft size={20} />
          Kembali ke Beranda
        </button>

        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#D4AF7A]/10 rounded-full mb-6">
            <MessageSquare className="w-8 h-8 text-[#D4AF7A]" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#F7F5F2] mb-6">
            Bagikan Pengalaman Anda
          </h1>
          <p className="text-lg text-[#F7F5F2]/70">
            Kami sangat menghargai testimoni Anda. Ceritakan pengalaman Anda dengan layanan Putri Henna Artist.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Form */}
          <div className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-[#F7F5F2] mb-2">
                  <User size={18} className="text-[#D4AF7A]" />
                  Nama Lengkap *
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Masukkan nama Anda"
                  className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A] transition-colors"
                />
              </div>

              {/* Role */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-[#F7F5F2] mb-2">
                  <Briefcase size={18} className="text-[#D4AF7A]" />
                  Role / Event *
                </label>
                <input
                  type="text"
                  required
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  placeholder="Contoh: Pengantin, Engagement, Prewedding, dll"
                  className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A] transition-colors"
                />
              </div>

              {/* Photo URL / Upload */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-[#F7F5F2] mb-3">
                  <ImageIcon size={18} className="text-[#D4AF7A]" />
                  Foto Profil (Opsional)
                </label>

                {/* Toggle Buttons */}
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => {
                      setImageSourceType('url');
                      setFileError('');
                    }}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all ${
                      imageSourceType === 'url'
                        ? 'bg-[#D4AF7A] text-black font-medium'
                        : 'bg-white/5 text-[#F7F5F2]/70 border border-[#D4AF7A]/30 hover:border-[#D4AF7A]/50'
                    }`}
                  >
                    <LinkIcon size={16} />
                    URL Link
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setImageSourceType('file');
                      setForm({ ...form, image: '' });
                      setFileError('');
                    }}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all ${
                      imageSourceType === 'file'
                        ? 'bg-[#D4AF7A] text-black font-medium'
                        : 'bg-white/5 text-[#F7F5F2]/70 border border-[#D4AF7A]/30 hover:border-[#D4AF7A]/50'
                    }`}
                  >
                    <Upload size={16} />
                    Upload File
                  </button>
                </div>

                {/* URL Input */}
                {imageSourceType === 'url' && (
                  <div>
                    <input
                      type="url"
                      value={form.image}
                      onChange={(e) => setForm({ ...form, image: e.target.value })}
                      placeholder="https://example.com/foto-anda.jpg"
                      className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A] transition-colors"
                    />
                    <p className="mt-2 text-xs text-[#F7F5F2]/50">
                      Masukkan URL foto profil Anda. Kosongkan untuk menggunakan avatar default.
                    </p>
                  </div>
                )}

                {/* File Upload */}
                {imageSourceType === 'file' && (
                  <div className="space-y-3">
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileUpload}
                        disabled={uploadingFile}
                        className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#D4AF7A]/10 file:text-[#D4AF7A] file:cursor-pointer hover:file:bg-[#D4AF7A]/20 focus:outline-none focus:border-[#D4AF7A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      {uploadingFile && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/5 rounded-lg">
                          <Loader2 className="w-6 h-6 text-[#D4AF7A] animate-spin" />
                        </div>
                      )}
                    </div>

                    {/* Error Message */}
                    {fileError && (
                      <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <p className="text-sm text-red-400">{fileError}</p>
                      </div>
                    )}

                    {/* Success Message */}
                    {form.image && !fileError && imageSourceType === 'file' && (
                      <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                        <p className="text-sm text-green-400">
                          ✓ Gambar berhasil dikonversi ke WebP
                        </p>
                      </div>
                    )}

                    <p className="text-xs text-[#F7F5F2]/50">
                      Format gambar apapun (JPG, PNG, dll) akan otomatis dikonversi ke WebP. Maksimal 2MB.
                    </p>
                  </div>
                )}
              </div>

              {/* Rating */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-[#F7F5F2] mb-3">
                  <Star size={18} className="text-[#D4AF7A] fill-[#D4AF7A]" />
                  Rating *
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onMouseEnter={() => setHoveredStar(star)}
                      onMouseLeave={() => setHoveredStar(0)}
                      onClick={() => setForm({ ...form, rating: star })}
                      className="p-2 transition-all hover:scale-110"
                    >
                      <Star
                        size={32}
                        className={`transition-colors ${
                          star <= (hoveredStar || form.rating)
                            ? 'text-[#D4AF7A]'
                            : 'text-[#F7F5F2]/30'
                        }`}
                        fill={star <= (hoveredStar || form.rating) ? '#D4AF7A' : 'none'}
                      />
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-sm text-[#F7F5F2]/50">
                  {form.rating === 1 && 'Kurang'}
                  {form.rating === 2 && 'Cukup'}
                  {form.rating === 3 && 'Biasa'}
                  {form.rating === 4 && 'Bagus'}
                  {form.rating === 5 && 'Sangat Baik!'}
                </p>
              </div>

              {/* Testimonial Text */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-[#F7F5F2] mb-2">
                  <MessageSquare size={18} className="text-[#D4AF7A]" />
                  Testimoni *
                </label>
                <textarea
                  required
                  rows={5}
                  maxLength={500}
                  value={form.text}
                  onChange={(e) => setForm({ ...form, text: e.target.value })}
                  placeholder="Ceritakan pengalaman Anda dengan layanan Putri Henna Artist..."
                  className="w-full px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A] resize-none transition-colors"
                />
                <div className="flex justify-between mt-2">
                  <p className="text-xs text-[#F7F5F2]/50">
                    Bagikan pengalaman jujur Anda tentang layanan kami
                  </p>
                  <p className="text-xs text-[#F7F5F2]/50">
                    {form.text.length}/500
                  </p>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!isFormValid || submitting}
                className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    Mengirim...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Kirim Testimoni
                  </>
                )}
              </button>

              <p className="text-xs text-[#F7F5F2]/50 text-center">
                Dengan mengirim testimoni, Anda setuju bahwa testimoni akan ditampilkan di website setelah melalui proses review.
              </p>
            </form>
          </div>

          {/* Preview */}
          <div className="space-y-6">
            <div className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-8">
              <h3 className="text-lg font-semibold text-[#F7F5F2] mb-6">Preview Testimoni Anda</h3>

              {/* Testimonial Card Preview */}
              <div className="bg-gradient-to-b from-[#0A0A0A] to-black border border-[#D4AF7A]/30 rounded-xl p-6">
                <div className="flex items-center gap-4 mb-4">
                  {/* Avatar Preview */}
                  <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#D4AF7A] bg-[#D4AF7A]/10 flex items-center justify-center">
                    {form.image ? (
                      <img src={form.image} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-8 h-8 text-[#D4AF7A]" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1">
                    <h4 className="text-[#F7F5F2] font-semibold">
                      {form.name || 'Nama Anda'}
                    </h4>
                    <p className="text-[#D4AF7A] text-sm">
                      {form.role || 'Role/Event'}
                    </p>
                  </div>

                  {/* Rating */}
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        size={16}
                        className={star <= form.rating ? 'text-[#D4AF7A]' : 'text-[#F7F5F2]/30'}
                        fill={star <= form.rating ? '#D4AF7A' : 'none'}
                      />
                    ))}
                  </div>
                </div>

                {/* Text */}
                <p className="text-[#F7F5F2]/80 leading-relaxed">
                  "{form.text || 'Testimoni Anda akan muncul di sini...'}"
                </p>
              </div>

              {/* Info Box */}
              <div className="mt-6 p-4 bg-[#D4AF7A]/5 border border-[#D4AF7A]/20 rounded-lg">
                <p className="text-sm text-[#F7F5F2]/70">
                  <strong className="text-[#D4AF7A]">Catatan:</strong> Testimoni yang Anda kirim akan melalui proses review terlebih dahulu sebelum ditampilkan di website. Ini biasanya memakan waktu 1-2 hari kerja.
                </p>
              </div>
            </div>

            {/* Tips Section */}
            <div className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-[#F7F5F2] mb-4">Tips untuk Testimoni yang Baik</h3>
              <ul className="space-y-3 text-sm text-[#F7F5F2]/70">
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF7A] mt-0.5">•</span>
                  <span>Jujur dan spesifik tentang pengalaman Anda</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF7A] mt-0.5">•</span>
                  <span>Sebutkan layanan apa yang Anda gunakan</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF7A] mt-0.5">•</span>
                  <span>Berikan feedback yang konstruktif</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF7A] mt-0.5">•</span>
                  <span>Pertahankan kesopanan dan respect</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicTestimonialForm;
